/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useMemo, useState } from 'react';
import {  LinearProgress, Pagination, Paper, Icon, IconButton, Theme, Table, TableBody, useMediaQuery, TableCell, TableContainer, TableFooter, TableHead, TableRow, useTheme, Typography, Checkbox,  Grid, Select, MenuItem, SelectChangeEvent,
  TextField, Snackbar, Alert, SnackbarCloseReason, Box,
  alpha, GlobalStyles,
  Card,
  Chip
} from '@mui/material';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDebounce } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { IPermissao, UsuarioService } from '../../../shared/services/api/cadastros/UsuariosService';
import { useDrawerContext } from '../../../shared/contexts';
import { LinhasEsqueletoTabela } from '../../../shared/components';
import NavTabsUsuario from './NavTabsUsuario';
import { 
  Security as SecurityIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Person as PersonIcon
} from '@mui/icons-material';

export const ListagemPermissao: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { id = '', nome = ''} = useParams();
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const theme = useTheme();
  const { debounce } = useDebounce();
  const [rows, setRows] = useState<IPermissao[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const { toggleDrawerOpen } = useDrawerContext();
  const [modulo, setModulo] = useState('Cadastro');
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');  
 
  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);
  
  const handleChangeBasic = (event: SelectChangeEvent) => {
    setModulo(event.target.value as string);
    setFilter('');
    UsuarioService.getAllPermissao(id, pagina, event.target.value, '')
      .then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          setTms('error');
          setMsg(result.message);
          setOpen(true);
        } else {
          setTotalCount(result.totalCount);
          setRows(result.data);
        }
      });
  };

  useEffect(() => {
    setIsLoading(true);
    debounce(() => {
      UsuarioService.getAllPermissao(id, pagina, modulo, filter)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpen(true);
          } else {
            setTotalCount(result.totalCount);
            setRows(result.data);
          }
        });
    });
  }, [pagina, filter, modulo, id, debounce]);

  function handleSave(id: string, permissoes: boolean) {
    UsuarioService
      .updateByIdPermissao(id, permissoes)
      .then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          setTms('error');
          setMsg(result.message);
          setOpen(true);
        } else {
          setTms('success');
          setMsg('Permissão alterada com sucesso!');
          setOpen(true);
          setIsLoading(false);
        }
      });
  }  

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    for (let i = 0; i < rows.length; i++) {
      if (rows[i].idpermissoes === e.target.id) {
        rows[i].permissao = e.target.checked;
        setSearchParams({pagina: pagina.toString() }, { replace: true });
        handleSave(e.target.id, e.target.checked);
        return;
      }
    }
  };

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);     
  };

  // Estatísticas para o cabeçalho
  const permissoesAtivas = rows.filter(row => row.permissao).length;
  const totalPermissoes = rows.length;

  return (
    <>
      {/* Estilos globais corrigidos para tema escuro */}
      <GlobalStyles
        styles={{
          'html, body, #root': {
            margin: 0,
            padding: 0,
            overflow: 'hidden',
            height: '100vh',
            backgroundColor: theme.palette.background.default
          },
          '*::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '*::-webkit-scrollbar-track': {
            background: theme.palette.mode === 'dark' 
              ? alpha(theme.palette.background.paper, 0.3)
              : alpha(theme.palette.grey[100], 0.8),
            borderRadius: '4px',
            margin: '4px 0'
          },
          '*::-webkit-scrollbar-thumb': {
            background: theme.palette.mode === 'dark'
              ? alpha(theme.palette.grey[600], 0.6)
              : alpha(theme.palette.grey[400], 0.6),
            borderRadius: '4px',
            border: theme.palette.mode === 'dark' 
              ? `2px solid ${alpha(theme.palette.background.paper, 0.3)}`
              : `2px solid ${alpha(theme.palette.grey[100], 0.8)}`,
            '&:hover': {
              background: theme.palette.mode === 'dark'
                ? alpha(theme.palette.grey[500], 0.8)
                : alpha(theme.palette.grey[500], 0.8),
            }
          },
          '*::-webkit-scrollbar-corner': {
            background: 'transparent'
          }
        }}
      />

      <Box sx={{ 
        p: { xs: 1, sm: 2, md: 2 },
        pb: { xs: 2, sm: 3, md: 3 }, // MARGEM INFERIOR ADICIONADA
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        background: theme.palette.background.default
      }}>
        
        {/* Header simplificado */}
        <Card 
          elevation={0}
          sx={{ 
            mb: 1.5,
            p: 1.5,
            background: theme.palette.background.paper,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            borderRadius: 2,
            flexShrink: 0
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1.5}>
              {smDown && (
                <IconButton 
                  onClick={toggleDrawerOpen} 
                  size="small"
                  sx={{
                    background: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.2),
                    }
                  }}
                >
                  <Icon fontSize="small">menu</Icon>
                </IconButton>
              )}

              <Box display="flex" alignItems="center" gap={1}>
                <PersonIcon 
                  sx={{ 
                    color: theme.palette.primary.main,
                    fontSize: { xs: 22, sm: 24 }
                  }} 
                />
                <Typography
                  variant="h6"
                  fontWeight="500"
                  color="text.primary"
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem' } }}
                >
                  {nome}
                </Typography>
              </Box>
            </Box>

            {/* Estatísticas compactas */}
            <Box display="flex" gap={0.5} flexWrap="wrap">
              <Chip 
                icon={<CheckCircleIcon />}
                label={`${permissoesAtivas}`}
                size="small"
                color="success"
                variant="outlined"
                sx={{ 
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  height: '24px',
                }}
              />
              <Chip 
                label={`${totalPermissoes}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ 
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  height: '24px',
                }}
              />
            </Box>
          </Box>
        </Card>

        {/* Card de Filtros compacto */}
        <Card 
          elevation={0}
          sx={{ 
            mb: 1.5,
            p: 1.5,
            background: theme.palette.background.paper,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            borderRadius: 2,
            flexShrink: 0
          }}
        >
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <Select
                size='small'
                fullWidth
                value={modulo}
                onChange={handleChangeBasic}
                sx={{ 
                  fontSize: '0.8rem',
                  '& .MuiSelect-select': {
                    py: 0.8
                  }
                }}
              >
                <MenuItem value={'Cadastro'} sx={{ fontSize: '0.8rem' }}>
                  Cadastros
                </MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                size='small'
                value={filter}
                placeholder={Environment.INPUT_DE_BUSCA}
                onChange={(e) => setFilter(e.target.value)}
                sx={{ 
                  fontSize: '0.8rem',
                  '& .MuiInputBase-input': {
                    fontSize: '0.8rem',
                    py: 0.8
                  }
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={12} md={5}>
              <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                {totalCount} permissão{totalCount !== 1 ? 'es' : ''} encontrada{totalCount !== 1 ? 's' : ''}
              </Typography>
            </Grid>
          </Grid>
        </Card>

        <Snackbar 
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }} 
          open={open} 
          autoHideDuration={2000} 
          onClose={handleClose}
        >
          <Alert
            onClose={handleClose}
            severity={tms}
            variant="filled"
            sx={{ 
              width: '100%',
              borderRadius: 1,
              fontWeight: 500,
              fontSize: '0.85rem'
            }}
          >
            {msg}
          </Alert>
        </Snackbar>

        {/* Container da tabela com margem inferior interna */}
        <Card 
          elevation={0}
          sx={{ 
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            borderRadius: 2,
            background: theme.palette.background.paper,
            overflow: 'hidden',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            mb: 0.5 // Pequena margem interna inferior
          }}
        >
          <NavTabsUsuario
            id={id}
            nome={nome}
            case={1}                
          />
          
          {/* TableContainer com padding inferior */}
          <TableContainer 
            sx={{ 
              flex: 1, 
              overflow: 'auto',
              pb: 0.5 // Pequeno padding inferior para não colar
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ 
                  background: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.dark, 0.1)
                    : alpha(theme.palette.primary.main, 0.04),
                  '& th': { 
                    fontWeight: '600', 
                    color: theme.palette.text.primary,
                    fontSize: '0.76rem',
                    py: 1,
                    px: 1,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px'
                  }
                }}>
                  <TableCell sx={{ width: '40%' }}>Descrição</TableCell>
                  <TableCell sx={{ width: '35%' }}>URL</TableCell>
                  <TableCell sx={{ width: '15%' }}>Método</TableCell>
                  <TableCell sx={{ width: '10%' }} align="center">Permissão</TableCell>
                </TableRow>
              </TableHead>

              {isLoading && rows.length === 0 && (
                <TableBody>
                  <LinhasEsqueletoTabela
                    colunas={[
                      { width: '80%' },
                      { width: '75%' },
                      { variant: 'rounded', width: 50, height: 20 },
                      { align: 'center', variant: 'circular', width: 20, height: 20 },
                    ]}
                  />
                </TableBody>
              )}
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    key={row.idpermissoes}
                    hover
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      background: index % 2 === 0 
                        ? 'transparent' 
                        : alpha(theme.palette.action.hover, 0.02),
                      '&:hover': {
                        background: alpha(theme.palette.action.hover, 0.04),
                      },
                      '& td': {
                        py: 0.8,
                        px: 1,
                        fontSize: '0.78rem',
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                      }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="500" fontSize="0.78rem">
                        {row.descricao}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        fontFamily="monospace" 
                        color="text.secondary" 
                        fontSize="0.75rem"
                        sx={{ 
                          background: alpha(theme.palette.grey[200], 0.3),
                          px: 0.8,
                          py: 0.3,
                          borderRadius: 0.5,
                          display: 'inline-block'
                        }}
                      >
                        {row.url}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={row.metodo}
                        size="small"
                        color={row.metodo === 'GET' ? 'primary' : row.metodo === 'POST' ? 'success' : row.metodo === 'PUT' ? 'warning' : 'error'}
                        variant="filled"
                        sx={{ 
                          fontWeight: '600',
                          fontSize: '0.7rem',
                          height: '20px',
                          minWidth: 50
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        id={row.idpermissoes}
                        checked={row.permissao} 
                        onChange={handleChange}
                        icon={<CancelIcon color="disabled" />}
                        checkedIcon={<CheckCircleIcon color="success" />}
                        sx={{ 
                          '& .MuiSvgIcon-root': { fontSize: 20 },
                          '&.Mui-checked': {
                            color: theme.palette.success.main,
                          }
                        }}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>

              {totalCount === 0 && !isLoading && (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={4} align='center' sx={{ py: 4 }}>
                      <Box textAlign="center">
                        <SecurityIcon sx={{ fontSize: 36, color: 'text.secondary', mb: 1, opacity: 0.5 }} />
                        <Typography variant='h6' color='text.secondary' gutterBottom fontWeight="500" fontSize="1rem">
                          {Environment.LISTAGEM_VAZIA}
                        </Typography>
                        <Typography variant='body2' color='text.secondary' fontSize="0.8rem">
                          Nenhuma permissão encontrada
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>

          {/* Footer da tabela com margem inferior adicional */}
          <Box sx={{ 
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            background: alpha(theme.palette.background.default, 0.6),
            p: 1,
            flexShrink: 0,
            mb: 5 // Margem inferior adicional
          }}>
            {isLoading && (
              <LinearProgress 
                sx={{
                  height: 1.5,
                  borderRadius: 0.5,
                  background: alpha(theme.palette.primary.main, 0.1),
                  '& .MuiLinearProgress-bar': {
                    background: theme.palette.primary.main
                  }
                }}
              />
            )}
            
            {(totalCount > 0 && totalCount > Environment.LIMITE_DE_LINHAS) && (
              <Box display='flex' justifyContent='center' alignItems='center' gap={2}>
                <Pagination
                  page={pagina}
                  count={Math.ceil(totalCount / Environment.LIMITE_DE_LINHASSMALL)}
                  onChange={(_, newPage) => setSearchParams({pagina: newPage.toString() }, { replace: true })}
                  color='primary'
                  size="small"
                  shape='rounded'
                  showFirstButton
                  showLastButton
                  sx={{
                    '& .MuiPaginationItem-root': {
                      fontWeight: '500',
                      fontSize: '0.75rem',
                      minWidth: 28,
                      height: 28,
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.1),
                      },
                      '&.Mui-selected': {
                        background: theme.palette.primary.main,
                        color: 'white',
                      }
                    }
                  }}
                />
                <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                  Página {pagina} de {Math.ceil(totalCount / Environment.LIMITE_DE_LINHAS)}
                </Typography>
              </Box>
            )}
          </Box>
        </Card>
      </Box>
    </>
  );
};